/**
 * Display-only glow helpers recovered from the historical WaveOrb
 * (layered radials, colored halo, per-color bloom). Crowd counts stay
 * weighted Feel data; this file only paints them as an orb.
 */

const BLOOM_POSITIONS = [
  '32% 28%',
  '72% 34%',
  '22% 72%',
  '78% 70%',
  '50% 48%',
];

export const NOTE_SYMBOLS = ['♪', '♫', '♬', '♩', '♭', '♯'];

/** Idle / empty orb. Not a Feel color. */
export const NEUTRAL_ORB_COLOR = '#1b2740';
export const NEUTRAL_NOTE_COLOR = '#8b9cb8';

export function orbPalette(colors) {
  if (Array.isArray(colors) && colors.length) return colors;
  return [NEUTRAL_ORB_COLOR];
}

export function orbHalo(colors, { restrained = false } = {}) {
  const palette = orbPalette(colors);
  if (restrained) {
    return `0 10px 28px rgba(0,0,0,0.55), 0 0 22px ${palette[0]}55`;
  }
  const layers = palette.map((color, index) => (
    `0 0 ${18 + index * 7}px ${color}88`
  ));
  return `0 8px 28px rgba(0,0,0,0.5), ${layers.join(', ')}`;
}

export function orbBloomLayers(colors) {
  const palette = orbPalette(colors);
  if (palette.length === 1) {
    return [{
      color: palette[0],
      position: '50% 42%',
    }];
  }
  return palette.map((color, index) => ({
    color,
    position: BLOOM_POSITIONS[index % BLOOM_POSITIONS.length],
  }));
}

export function orbCoreBackground(colors) {
  const palette = orbPalette(colors);
  if (palette.length === 1) {
    return [
      `radial-gradient(circle at 38% 30%, rgba(255,255,255,0.22), transparent 34%)`,
      `radial-gradient(circle at 50% 48%, ${palette[0]}cc, ${palette[0]}66 46%, rgba(8,16,41,0.92) 74%)`,
    ].join(', ');
  }
  const blooms = palette.map((color, index) => (
    `radial-gradient(circle at ${BLOOM_POSITIONS[index % BLOOM_POSITIONS.length]}, ${color}99 0%, transparent 46%)`
  ));
  return [
    'radial-gradient(circle at 38% 30%, rgba(255,255,255,0.16), transparent 32%)',
    ...blooms,
    'radial-gradient(circle at 50% 50%, rgba(8,16,41,0.18), rgba(8,16,41,0.78) 78%)',
  ].join(', ');
}

export function orbAnchorPositions(count = 5, radius = 46) {
  const anchors = [];
  for (let index = 0; index < count; index += 1) {
    const angle = -90 + (index * 360) / count;
    const radians = (angle * Math.PI) / 180;
    anchors.push({
      left: `${50 + Math.cos(radians) * radius}%`,
      top: `${50 + Math.sin(radians) * radius}%`,
    });
  }
  return anchors;
}

export function orbNotePositions(count = 6, radius = 41) {
  const notes = [];
  for (let index = 0; index < count; index += 1) {
    const angle = ((index / count) * 360) - 22;
    const radians = (angle * Math.PI) / 180;
    notes.push({
      left: `${50 + Math.cos(radians) * radius}%`,
      top: `${50 + Math.sin(radians) * radius}%`,
    });
  }
  return notes;
}

export function hexToRgb(hex) {
  const value = String(hex || '').replace('#', '');
  if (value.length !== 6) return null;
  const red = Number.parseInt(value.slice(0, 2), 16);
  const green = Number.parseInt(value.slice(2, 4), 16);
  const blue = Number.parseInt(value.slice(4, 6), 16);
  if (![red, green, blue].every((channel) => Number.isFinite(channel))) return null;
  return { r: red, g: green, b: blue };
}

function channelToHex(channel) {
  return Math.max(0, Math.min(255, Math.round(channel))).toString(16).padStart(2, '0');
}

/**
 * Weight-aware mix in linear RGB, then a light lift so two Feels do not
 * collapse into a muddy mid-tone. Display only.
 */
export function mixFeelColors(weightedColors) {
  const items = (weightedColors || []).filter((item) => item?.color && item.weight > 0);
  if (items.length === 0) return NEUTRAL_ORB_COLOR;
  if (items.length === 1) return items[0].color;

  let red = 0;
  let green = 0;
  let blue = 0;
  let total = 0;
  items.forEach((item) => {
    const rgb = hexToRgb(item.color);
    if (!rgb) return;
    red += rgb.r * rgb.r * item.weight;
    green += rgb.g * rgb.g * item.weight;
    blue += rgb.b * rgb.b * item.weight;
    total += item.weight;
  });
  if (total <= 0) return items[0].color;

  const mixed = {
    r: Math.sqrt(red / total),
    g: Math.sqrt(green / total),
    b: Math.sqrt(blue / total),
  };
  const lift = 0.18;
  return `#${channelToHex(mixed.r + (255 - mixed.r) * lift)}${channelToHex(mixed.g + (255 - mixed.g) * lift)}${channelToHex(mixed.b + (255 - mixed.b) * lift)}`;
}

export function feelAnchorWeight(count, submittedCount) {
  const total = Number(submittedCount);
  const value = Number(count);
  if (!Number.isFinite(total) || total <= 0 || !Number.isFinite(value) || value <= 0) {
    return 0;
  }
  return value / total;
}
